import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { AppImage } from '@/components/atoms';
import { CLIENT_LOGO_BOX, clientLogo } from '@/config/clientLogos';
import { type Client, clientMonogram } from '@/content/clients';
import { fitLogoBox } from '@/utils/logoBox';

/**
 * How wide a monogram chip is allowed to run, and why it is not a circle.
 *
 * The monograms are two to five characters — "CU" next to "MLHUD" — so a fixed
 * square would either crop the long ones or leave the short ones swimming. A
 * min-width with horizontal padding gives every chip the same height and the
 * same optical weight while letting the wide ones be wide, which is how a
 * real wordmark behaves in the same box.
 */
const CHIP_MIN_WIDTH = 56;

/**
 * A client's logo, sized to the same visual weight as every other logo on the
 * wall (see `fitLogoBox`).
 *
 * Until the artwork for a client is in `src/assets/clients/`, a monogram chip
 * stands in its place. That is the whole reason the wall can ship before the
 * logo files do — most of the roster has no file yet — and a
 * missing one shows up as a deliberate mark rather than a hole in the row.
 *
 * The chip is initials and not the full name, which it used to be. At this many
 * clients a wall of wordmarks is a wall of running text: no two tiles have the
 * same optical weight, the long names dominate the short ones, and nothing in
 * the row reads as a logo. A monogram occupies the box a logo will occupy, so
 * the band's rhythm is the same before and after the artwork lands — and the
 * name is never lost, because the tile captions it underneath in every case.
 */
export function ClientMark({ client }: { client: Client }) {
  const logo = clientLogo(client);

  if (!logo) {
    return (
      <Box
        sx={{
          minWidth: CHIP_MIN_WIDTH,
          px: 1.5,
          py: 0.75,
          borderRadius: 1.5,
          // On the tile's white, this reads as a placed mark rather than as a
          // disabled control. It is deliberately not the brand red: a wall of
          // red chips would make the band louder than anything else on the
          // page, and these stand in for other companies' marks, not ours.
          bgcolor: 'grey.100',
          border: 1,
          borderColor: 'grey.300',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Typography
          component="span"
          sx={{
            fontFamily: 'h1.fontFamily',
            fontWeight: 700,
            // The long monograms are set smaller so a five-character chip is
            // the same width as a two-character one is tall.
            fontSize: { xs: '1rem', md: '1.125rem' },
            lineHeight: 1,
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
            color: 'grey.700',
          }}
        >
          {clientMonogram(client)}
        </Typography>
      </Box>
    );
  }

  // An SVG is left alone by the build, so it is sized by the box directly.
  if (typeof logo === 'string') {
    return (
      <img
        src={logo}
        alt={client.name}
        width={CLIENT_LOGO_BOX.maxWidth}
        height={CLIENT_LOGO_BOX.maxHeight}
        loading="lazy"
        decoding="async"
        style={{
          width: CLIENT_LOGO_BOX.maxWidth,
          height: CLIENT_LOGO_BOX.maxHeight,
          objectFit: 'contain',
        }}
      />
    );
  }

  const { width, height } = fitLogoBox(logo.img.w, logo.img.h, CLIENT_LOGO_BOX);
  return (
    <AppImage
      picture={logo}
      alt={client.name}
      sizes={`${width}px`}
      style={{ width, height, objectFit: 'contain' }}
    />
  );
}
